import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {Component, ErrorInfo, ReactNode} from 'react';

import {FONT_PROXIMA_15} from 'styles/FontConstants';
import {RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

/** @package */
export const ERROR_MESSAGE =
  'An unexpected error occurred trying to display this analysis. ' +
  'Please try clicking a different view and refreshing your page. If the problem persists, ' +
  'please reach out to Benchling Support.';

interface AnalysisComponentErrorBoundaryProps<ErrorExtras extends Record<string, unknown>> {
  className?: string;
  children: ReactNode;
  errorExtras: ErrorExtras;
  actionContent?: ReactNode;
}

interface AnalysisComponentErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component for components used by `AnalysisUISkeleton`.
 *
 * If we fail to render underlying components, this error boundary should avoid having the entire Analysis
 * crash, crash, which should allow the user to click away, and offer a smoother failure UX. This can happen
 * if we select an invalid `AnalysisMainViewItem` for example.
 */
export default class AnalysisComponentErrorBoundary<
  ErrorExtras extends Record<string, unknown>,
> extends Component<AnalysisComponentErrorBoundaryProps<ErrorExtras>, AnalysisComponentErrorBoundaryState> {
  constructor(props: AnalysisComponentErrorBoundaryProps<ErrorExtras>) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError(error: Error): AnalysisComponentErrorBoundaryState {
    return {hasError: true, error};
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logging.error(error, {
      extra: {...this.props.errorExtras, errorInfo},
    });
  }

  override componentDidUpdate(
    prevProps: AnalysisComponentErrorBoundaryProps<ErrorExtras>,
    prevState: AnalysisComponentErrorBoundaryState
  ): void {
    if (prevProps.children !== this.props.children && prevState.hasError) {
      this.setState({hasError: false});
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          data-test-id="analysis-component-error-boundary-wrapper"
          className={classNames(this.props.className, css(styles.wrapper))}
        >
          <BenchlingIcon icon="error" className={css(styles.icon)} />
          <p className={css(styles.errorMessage)}>{ERROR_MESSAGE}</p>
          {this.props.actionContent}
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  icon: {
    width: 48,
    height: 48,
    marginBottom: 15,
    color: RED,
  },
  errorMessage: {
    ...FONT_PROXIMA_15,
    textAlign: 'center',
    maxWidth: '400px',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
});
